// src/lib/demoCacheReset.js — a demo visitor's browser cache must never serve
// business-list/entity reads from a superseded scenario (Tidewater) after a
// reseed makes Eighty Days primary. Scoped NARROWLY, in two independent ways:
//
//   • By BUCKET — ONLY the 'list' and 'entity' entityCache/persistentCache
//     buckets — never 'body'/'thread' (a reseed never touches saved email
//     bodies or threads) and never Janet's own conversation state
//     (janet_chat_* lives server-side in the demo DB, never in this cache).
//   • By KEY, within those buckets — only the exact business-list query-cache
//     PREFIXES and entity-cache KEYS a scenario cutover can actually affect
//     (AFFECTED_LIST_PREFIXES / affectedEntityKeys below), never every key the
//     bucket happens to hold. An independent probe seeded unrelated list/
//     entity/recent-visit entries alongside the scenario's own and found them
//     deleted by the previous blanket clearType()/clearPersistentType() pass
//     — that is the defect this file now closes.
//
//   • ONLY in demo mode. Production never calls this.
//   • The version marker is written ONLY after every clear call reports
//     success WITHIN ITS TIME BOUND — a failed OR timed-out clear leaves the
//     OLD version on disk, so the next boot retries rather than silently
//     claiming done.
//   • A clear that cannot prove it finished (failed or timed out) also makes
//     THIS boot's hydration skip exactly the same affected keys — leaving the
//     version marker behind guarantees a retry NEXT boot, but does nothing to
//     stop the current boot's own IndexedDB hydration from reading the same
//     stale row straight back out of disk a moment later.
//
// Runs once, synchronously-sequenced in src/main.jsx BEFORE initPersistentCache()
// hydrates entityCache from IndexedDB — clearing the in-memory store but
// leaving stale rows on disk would just have hydrate() read them straight
// back in a moment later.
import { IS_DEMO } from './demoClient.js'
import { DEMO_CONTENT_VERSION } from './demoContentVersion.js'
import { clearMatching } from './entityCache.js'
import { clearPersistentMatching } from './persistentCache.js'
import { DEMO_IDS, EIGHTY_DAYS_IDS } from './demoScenes.js'

const VERSION_KEY = 'chatrone.demo.contentVersion'
const RECENT_VISITS_KEY = 'chatrone.recentVisits.v1'
// Every cache bucket a stale scenario's business records could be sitting in.
// Deliberately excludes 'body' and 'thread' — a reseed never writes those.
export const BUSINESS_CACHE_TYPES = ['list', 'entity']

// The 'list' bucket's query-cache keys (src/lib/queryCache.js, read by
// useProperties/useProjects/useDeals/useTasks/Dashboard's DueToday) are
// `${table}:${filters...}` — only these tables' list reads could contain a
// retired Tidewater row or be missing a newly-primary Eighty Days one.
// Anything else (contacts, companies, inbox, invoices, …) is untouched by a
// scenario cutover and must survive a reset unconditionally.
export const AFFECTED_LIST_PREFIXES = ['properties:', 'projects:', 'deals:', 'tasks:', 'dashboard:due-today:']

// The 'entity' bucket's keys are `${entityType}:${id}` (src/lib/entityCache.js
// entityKey) — only 'deals' and 'projects' single-record caches exist today
// (useDeals.js/useProjects.js); 'properties' has no entity-cache reader, so
// there is nothing to name there. Named by the exact ids this scenario
// cutover actually touches — both the retired Tidewater rows AND the newly
// primary Eighty Days rows, read from the same literal ids demoScenes.js
// already carries (no guessing, no prefix match on the whole bucket).
function affectedEntityKeys() {
  return new Set([
    `deals:${DEMO_IDS.deal}`,
    `deals:${EIGHTY_DAYS_IDS.deal}`,
    `projects:${DEMO_IDS.project}`,
    `projects:${DEMO_IDS.gnProject}`,
    `projects:${DEMO_IDS.verticalProject}`,
    `projects:${EIGHTY_DAYS_IDS.gnProject}`,
    `projects:${EIGHTY_DAYS_IDS.verticalProject}`,
    `projects:${EIGHTY_DAYS_IDS.pilotProject}`,
    `projects:${EIGHTY_DAYS_IDS.animationProject}`,
    `projects:${EIGHTY_DAYS_IDS.featureProject}`,
  ])
}

// Exactly the (type, id) pairs recentVisits.js's recordVisit() could have
// stored for the retired Tidewater primary scenario — a "Jump back in" entry
// pointing at any of these is now pointing at a superseded record. Nothing
// else in the ring buffer is touched: an unrelated visit (a different
// property, a contact, a company) survives a reset unconditionally.
function retiredRecentVisitRefs() {
  return new Set([
    `property:${DEMO_IDS.property}`,
    `project:${DEMO_IDS.project}`,
    `project:${DEMO_IDS.gnProject}`,
    `project:${DEMO_IDS.verticalProject}`,
    `deal:${DEMO_IDS.deal}`,
  ])
}

function predicateFor(type) {
  if (type === 'list') {
    return (key) => AFFECTED_LIST_PREFIXES.some((p) => key.startsWith(p))
  }
  if (type === 'entity') {
    const keys = affectedEntityKeys()
    return (key) => keys.has(key)
  }
  return () => false
}

/** Pure: true when the stored version string is not today's. Takes the
 *  stored value as an argument (rather than reading localStorage itself) so
 *  it is testable with no DOM/storage. */
export function isStaleDemoVersion(storedVersion, currentVersion = DEMO_CONTENT_VERSION) {
  return storedVersion !== currentVersion
}

function readStoredVersion() {
  try { return localStorage.getItem(VERSION_KEY) } catch { return null }
}

function writeStoredVersion(version) {
  try { localStorage.setItem(VERSION_KEY, version) } catch { /* private mode */ }
}

// Bounds a single IndexedDB clear call so a wedged transaction can never hang
// boot indefinitely — nothing downstream of resetDemoCachesIfStale() in
// src/main.jsx bounds it, so the bound has to live here. Resolves
// `{ timedOut: true }` on expiry (never rejects, never leaves the caller
// waiting past RESET_TIMEOUT_MS) or `{ timedOut: false, value }` on genuine
// settlement — a rejection from the clear call itself is treated as a plain
// `false` value, same as clearPersistentMatching's own internal try/catch.
const RESET_TIMEOUT_MS = 2000

function withBound(promise, ms) {
  return new Promise((resolve) => {
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      resolve({ timedOut: true })
    }, ms)
    Promise.resolve(promise).then(
      (value) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        resolve({ timedOut: false, value })
      },
      () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        resolve({ timedOut: false, value: false })
      },
    )
  })
}

/**
 * Clears the specific business-list/entity cache keys (in-memory + IndexedDB)
 * and known-retired recent-visit entries when the demo's seeded scenario
 * content has moved since this browser last recorded a version — a prior
 * visit's cached Tidewater reads must never surface under the new Eighty Days
 * primary scenario. A no-op, instantly, outside demo mode or when the stored
 * version already matches.
 *
 * @returns {Promise<{
 *   reset: boolean, types?: string[], complete?: boolean, timedOut?: boolean,
 *   skip?: (type: string, key: string) => boolean
 * }>} `skip`, present only when `reset` is true, names exactly the keys THIS
 * boot's IndexedDB hydration must exclude — a no-op function when `complete`
 * is true (everything was actually removed from IndexedDB, so there is
 * nothing left to protect against), or the same affected-key predicate used
 * for the clear when `complete` is false (a failure or a timeout proves
 * nothing about whether the row is still on disk, so it must not be
 * hydrated back in this boot regardless).
 */
export async function resetDemoCachesIfStale() {
  if (!IS_DEMO) return { reset: false }
  const stored = readStoredVersion()
  if (!isStaleDemoVersion(stored)) return { reset: false }

  // In-memory first (synchronous, can't fail) — scoped to the exact affected
  // keys, never every key the bucket holds, so a render that happens to fire
  // mid-reset never reads a stale scenario row back out, and an unrelated
  // module's cached data is never touched at all.
  for (const type of BUSINESS_CACHE_TYPES) clearMatching(type, predicateFor(type))

  // IndexedDB next, each bucket's clear individually time-bounded. Only stamp
  // the new version once every bucket actually cleared within its bound — a
  // failure or a timeout here must NOT be reported as done, or the next boot
  // would skip the retry and a stale row could survive on disk indefinitely.
  const outcomes = await Promise.all(
    BUSINESS_CACHE_TYPES.map((type) => withBound(clearPersistentMatching(type, predicateFor(type)), RESET_TIMEOUT_MS)),
  )
  const timedOut = outcomes.some((o) => o.timedOut)
  const allCleared = !timedOut && outcomes.every((o) => o.value === true)

  // A scenario reseed means any "Jump back in" entry recorded before it,
  // naming one of the retired refs, is now pointing at a superseded record.
  // FILTER, never wipe — an unrelated visit (a different property, a
  // contact, a company) must survive. Best-effort, like the rest of
  // recentVisits.js's own storage access.
  try {
    const raw = localStorage.getItem(RECENT_VISITS_KEY)
    if (raw) {
      const retired = retiredRecentVisitRefs()
      const list = JSON.parse(raw).filter((v) => !retired.has(`${v?.type}:${v?.id}`))
      localStorage.setItem(RECENT_VISITS_KEY, JSON.stringify(list))
    }
  } catch { /* private mode / malformed JSON — leave recents as they are */ }

  if (allCleared) writeStoredVersion(DEMO_CONTENT_VERSION)

  const skip = allCleared
    ? () => false
    : (type, key) => BUSINESS_CACHE_TYPES.includes(type) && predicateFor(type)(key)

  return { reset: true, types: BUSINESS_CACHE_TYPES, complete: allCleared, timedOut, skip }
}
